import { useEffect, useCallback, useState } from 'react'
import moment from 'moment'
import { useCookies } from 'react-cookie'
import { useHistory } from 'react-router-dom'
import { AxiosError } from 'axios'
import axios from 'src/api'

import useIsLoggedIn from 'src/hooks/useIsLoggedIn'
import useMobile from 'src/hooks/useMobile'
import useSubscribed from 'src/hooks/useSubscribed'
import { createAffiliateClick } from './useAffiliateClick'

const useFlagAffiliate = (affiliateId, adId = null) => {
  const history = useHistory()
  const [cookies, setCookie, removeCookie] = useCookies(['affiliate', 'campaign', 'aff_post', 'utm_id'])
  const [loadingAffCheck, setLoadingAffCheck] = useState(false)
  const [targetDate, setTargetDate] = useState('July 14, 2022 23:59:00 PST')

  const [flagError, setFlagError] = useState('')
  const [affiliate, setAffiliate] = useState(null)
  const [campaign, setCampaign] = useState(null)
  const [affiliateVideo, setAffiliateVideo] = useState(null)
  const [affiliateAd, setAffiliateAd] = useState(null)

  const [isLoggedIn, loading] = useIsLoggedIn()
  const { isPremiumSubscribed } = useSubscribed()

  const flagAccountWithAffiliate = useCallback(async () => {
    if (affiliateId) {
      try {
        const res = await axios.post('/affiliate/flag', {
          affiliateId,
          adId
        })
        
        // on success, prompt for subscription
        if (res.data.success) {
          history.push('/memberships/premium/subscribe')
        }
      } catch (err) {
        const error = err as AxiosError
        setFlagError(error.response?.data?.message)
        if (error.response?.data?.message === 'You have already flagged this account') {
          history.push('/memberships/premium/subscribe')
        } else if (error.response?.data?.message) history.push('/')
      }
    }
  }, [affiliateId, adId])

  useEffect(() => {
    if (loading) return
    // if logged in and not premium, flag account with affiliate id and prompt to upgrade
    if (isLoggedIn && !isPremiumSubscribed) {
      flagAccountWithAffiliate()
    } else if (!isLoggedIn && (affiliateId || adId)) {
      setLoadingAffCheck(true)
      axios.get('/affiliate/active', { params: { affiliateId, adId } })
        .then(async (res) => {
          if (res.data.success) {
            setCampaign(res.data.campaign)
            setAffiliateVideo(res.data.affiliateVideo)
            setAffiliateAd(res.data.affiliateAd)

            await createAffiliateClick(affiliateId, res.data.affiliateVideo?.id, adId)

            // remove the cookie if the affiliate video is set to no-discount as plan
            if (res.data?.affiliateVideo?.plan === 'no-discount') {
              removeCookie('affiliate')
              setCookie('aff_post', res.data.affiliateVideo?.id, { maxAge: 30 * 24 * 60 * 60, path: '/' })
              history.push('/')
              return location.reload()
            }

            // only set the affiliate if we passed in an affiliateId
            if (affiliateId) {
              setAffiliate(res.data.affiliate)
              // if not logged in, set cookie for 30 days
              setCookie(res.data.affiliate ? 'affiliate' : 'campaign', affiliateId, { maxAge: 30 * 24 * 60 * 60, path: '/' })
            } else {
              removeCookie('affiliate')
            }

            if (res.data.affiliate) {
              removeCookie('aff_post')
            }

            if (res.data.affiliateVideo) {
              setCookie('aff_post', res.data.affiliateVideo?.id, { maxAge: 30 * 24 * 60 * 60, path: '/' })
            }

            if (res.data.affiliateAd) {
              setCookie('utm_id', res.data.affiliateAd?.ad_id, { maxAge: 30 * 24 * 60 * 60, path: '/' })
            }
            
            // nullify other cookie in case
            removeCookie(res.data.affiliate ? 'campaign' : 'affiliate')
            const newTargetDate = res.data.campaign?.end || 'July 14, 2099 23:59:00 PST'
            setTargetDate(newTargetDate)

            if (newTargetDate && moment(newTargetDate).isBefore(moment())) {
              // redirect to / if location contains "join"
              if (history.location.pathname.includes('join')) {
                history.push('/')
              }
            }

            // history.push('/register')
          } else {
            setFlagError('Invalid affiliate id')
            // redirect to / if location contains "join"
            if (history.location.pathname.includes('join')) {
              history.push('/')
              location.reload()
            }
          }
          setLoadingAffCheck(false)
        })
        .catch((err) => {
          console.error(err)
          setFlagError('Invalid affiliate id')
          setLoadingAffCheck(false)
          // redirect to / if location contains "join"
          if (history.location.pathname.includes('join')) {
            history.push('/')
            location.reload()
          }
        })
    }
  }, [isLoggedIn, loading])

  return {
    loading: loadingAffCheck,
    targetDate,
    error: flagError,
    affiliate,
    campaign,
    affiliateVideo,
    affiliateAd
  }
}

export default useFlagAffiliate
